/**
 * Strong password generator used by the register / reset-password screens.
 *
 * Why this exists at all: on Safari (macOS & iOS) the iCloud Keychain
 * «Использовать надёжный пароль» popup is triggered by
 * `autoComplete="new-password"` and ends up intercepting focus before
 * the keyboard can open. To unblock manual signups we disable the
 * autoComplete hint on those forms (CORE-54 Safari signup repro,
 * 2026-06-05) — which also removes Safari's own «strong password»
 * suggestion. This generator gives users an equivalent one-click way
 * to fill the field with a strong value.
 *
 * Strength: uses Web Crypto (`crypto.getRandomValues`) when available
 * — that's the case on every browser we ship to and on RN with the
 * `react-native-get-random-values` polyfill we already pull in via
 * other dependencies. Falls back to `Math.random` only if neither is
 * present, so the function never throws on environments we don't
 * fully control (third-party WebViews, very old runtimes).
 *
 * The result is guaranteed to contain at least one character from each
 * category (lower, upper, digit, special) so it passes the typical
 * server-side strength check we (and most BEs) enforce regardless of
 * how the random shuffle lands.
 */

const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
// Limited set of special characters — chosen to be safe across BE
// validation rules and copy-paste-friendly (avoiding quotes/backticks/
// backslashes that some forms strip or escape incorrectly).
const SPECIAL = '!@#$%^&*-_=+?';
const ALL = LOWER + UPPER + DIGITS + SPECIAL;

function randomInt(maxExclusive: number): number {
  // Prefer cryptographic randomness when the runtime exposes Web Crypto.
  // Both modern browsers (≥ IE11) and RN with the standard polyfill do.
  const cryptoObj: Crypto | undefined =
    typeof globalThis !== 'undefined' && (globalThis as any).crypto
      ? (globalThis as any).crypto
      : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    cryptoObj.getRandomValues(buf);
    // Modulo bias is negligible for ALL.length (~70) vs 2^32.
    return buf[0] % maxExclusive;
  }
  return Math.floor(Math.random() * maxExclusive);
}

/**
 * Generates a strong password of the requested length (default 16).
 *
 * Guarantees:
 *   - Length >= max(4, requested length). The function silently bumps
 *     anything below 4 up to 4 so we can include one character from
 *     each required category (the floor matches no known sign-up flow
 *     in the app — included only for defensive correctness).
 *   - Contains at least one lower-case, one upper-case, one digit and
 *     one special character.
 */
export function generateStrongPassword(length: number = 16): string {
  const safeLength = Math.max(4, Math.floor(length));

  // Seed with one guaranteed character per category.
  const required: string[] = [
    LOWER[randomInt(LOWER.length)],
    UPPER[randomInt(UPPER.length)],
    DIGITS[randomInt(DIGITS.length)],
    SPECIAL[randomInt(SPECIAL.length)],
  ];

  // Fill the rest from the combined pool.
  const filler: string[] = [];
  for (let i = required.length; i < safeLength; i++) {
    filler.push(ALL[randomInt(ALL.length)]);
  }

  // Fisher-Yates shuffle so the required characters don't always
  // appear at the start — otherwise the first four positions would
  // be predictable (lower-upper-digit-special).
  const result = [...required, ...filler];
  for (let i = result.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result.join('');
}
